'use client'

import { memo } from 'react'
import { Pencil, Square, CheckSquare, Star } from 'lucide-react'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import { getImageUrl } from '@/lib'
import { Tooltip } from '@/components/ui/Tooltip'
import type { StoredImage } from '@/types/image'

interface ImageCardProps {
  image: StoredImage
  isSelected: boolean
  isMultiSelected?: boolean
  /** Number of times this image appears in the current picker selection (duplicate mode) */
  selectionCount?: number
  onEdit: () => void
  onSelect: () => void
  onMultiSelect?: () => void
  onToggleFavorite?: () => void
  isNew?: boolean
  /** Passed to next/image for correct CDN hinting — overrides default responsive sizes */
  sizes?: string
}

export const ImageCard = memo(function ImageCard({
  image,
  isSelected: _isSelected,
  isMultiSelected = false,
  selectionCount,
  onEdit,
  onSelect,
  onMultiSelect,
  onToggleFavorite,
  isNew = false,
  sizes = '(max-width: 640px) 33vw, (max-width: 768px) 25vw, (max-width: 1024px) 20vw, (max-width: 1280px) 16vw, 12.5vw',
}: ImageCardProps) {
  // isSelected is kept in the interface for potential future single-select visual feedback
  void _isSelected

  const showAsSelected =
    isMultiSelected || (selectionCount !== undefined && selectionCount > 0)

  const handleClick = () => {
    if (onMultiSelect) onMultiSelect()
    else onSelect()
  }

  return (
    <div
      onClick={handleClick}
      className={cn(
        'relative group rounded-lg overflow-hidden cursor-pointer',
        'border-2 transition-all duration-200',
        'hover:shadow-lg hover:scale-[1.02]',
        showAsSelected
          ? 'border-primary ring-2 ring-primary/30'
          : 'border-border hover:border-primary/50'
      )}
    >
      <div className="aspect-square bg-muted relative">
        <Image
          src={getImageUrl(image.imageUrl, 'preview')}
          alt={image.filename}
          fill
          loading="lazy"
          className="object-cover transition-opacity duration-300"
          sizes={sizes}
          placeholder="blur"
          blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+"
          unoptimized
        />
      </div>

      {onToggleFavorite && (
        <Tooltip
          content={
            image.isFavorite ? 'Remove from favorites' : 'Favorite this photo'
          }
          side="bottom"
        >
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleFavorite()
            }}
            className={cn(
              'absolute top-1 left-1 sm:top-2 sm:left-2 p-1 sm:p-1.5 rounded shadow-sm',
              'transition-all duration-150 z-10 touch-manipulation cursor-pointer',
              image.isFavorite
                ? 'bg-amber-500 text-white hover:bg-amber-600'
                : 'bg-white/80 hover:bg-white text-muted-foreground hover:text-amber-500 border border-border/50'
            )}
          >
            <Star
              className={cn(
                'w-3 h-3 sm:w-3.5 sm:h-3.5',
                image.isFavorite && 'fill-current'
              )}
            />
          </button>
        </Tooltip>
      )}

      {/* Edit button — visible on hover/focus; always tappable on touch */}
      <Tooltip content="Edit this photo" side="bottom">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
          className={cn(
            'absolute top-1 right-1 sm:top-2 sm:right-2 p-1 sm:p-1.5 rounded shadow-sm',
            'border border-border/50 transition-all duration-150 z-10',
            'touch-manipulation',
            'md:opacity-0 md:group-hover:opacity-100 focus:opacity-100',
            'bg-white/80 hover:bg-white text-muted-foreground hover:text-foreground cursor-pointer'
          )}
        >
          <Pencil className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </button>
      </Tooltip>

      {onMultiSelect && (
        <Tooltip
          content={
            selectionCount && selectionCount > 0
              ? `Selected ${selectionCount} time${selectionCount > 1 ? 's' : ''}`
              : 'Select this photo'
          }
          side="top"
        >
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              onMultiSelect?.()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                onMultiSelect?.()
              }
            }}
            className={cn(
              'absolute bottom-1 right-1 sm:bottom-2 sm:right-2 rounded shadow-sm',
              'transition-all duration-150 z-10 cursor-pointer',
              showAsSelected
                ? 'bg-primary text-primary-foreground'
                : 'bg-white/80 text-muted-foreground border border-border/50',
              selectionCount && selectionCount > 1
                ? 'px-1.5 py-0.5 sm:px-2 sm:py-1'
                : 'p-1 sm:p-1.5'
            )}
          >
            {selectionCount && selectionCount > 1 ? (
              <span className="text-[10px] sm:text-xs font-semibold">
                ×{selectionCount}
              </span>
            ) : showAsSelected ? (
              <CheckSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            ) : (
              <Square className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            )}
          </div>
        </Tooltip>
      )}

      {isNew && (
        <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 px-1 py-0.5 bg-emerald-500 text-white text-[10px] sm:text-xs font-medium rounded shadow-sm z-10">
          NEW
        </div>
      )}
    </div>
  )
})
